"use client";

import { useRef } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { motion, useInView, useReducedMotion } from "@/lib/motion";
import { FREE_TRIAL_LABEL, FREE_TRIAL_URL, GREEN, ROUTES } from "@/lib/constants";
import { ArrowRight, Fingerprint, type LucideIcon } from "@suite/components/icons";
import { cn } from "@suite/lib/utils";

// Proxidize CTA for tool pages. The visual reuses the homepage "Our Products" 3D
// language: an isometric scanner pad with a sweeping scan line, blueprint dot grid,
// ghost wireframe layers and the breathing green glow.
//
// Two layouts: "rail" (sticky vertical card for the right column beside the results) and
// "horizontal" (stage on the left, copy on the right, used in the fingerprint hero).
//
// Variants are resolved by name inside this client component (icon components can't
// cross the server → client prop boundary), so pages just pass `variant="..."`.

interface CtaVariant {
  icon: LucideIcon;
  readoutLabel: string;
  readoutValue: string;
  heading: string;
  body: string;
}

const FINGERPRINT_CTA: CtaVariant = {
  icon: Fingerprint,
  readoutLabel: "NETWORK",
  readoutValue: "SEPARATE",
  heading: "Browser and network signals are separate layers.",
  body: "Changing an IP does not rewrite browser signals. Mobile and residential proxies address the network layer when that is what your workflow needs.",
};

interface StageProps {
  icon: LucideIcon;
  readoutLabel: string;
  readoutValue: string;
  active: boolean;
  reduced: boolean;
  /** The blueprint dot grid behind the pad. Off in the hero — it already sits on the hero's
   * own dot backdrop, and doubled dots read as noise. */
  dotGrid?: boolean;
  /** Tilts the pad the other way (rotateZ +35° instead of −35°) so it faces content on its
   * left when staged at a layout's right edge. A plane rotation, not scaleX(-1), which would
   * mirror the icon and render the readout text backwards. */
  mirrored?: boolean;
  className?: string;
}

function ScannerStage({
  icon: Icon,
  readoutLabel,
  readoutValue,
  active,
  reduced,
  dotGrid = true,
  mirrored = false,
  className,
}: StageProps) {
  return (
    /* The scanner is a device mock-up: hardware greys, white-alpha bevels and a
       glowing pad. It stays dark on both themes like a product photo, so it is
       pinned `dark-fixed` while the CTA card around it still flips. */
    <div className={cn("dark-fixed bg-background relative overflow-hidden", className)}>
      {dotGrid && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(247,247,248,0.06) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />
      )}
      {/* Ambient glow behind the pad — sized to fade out before it reaches the stage's edges,
       * so it reads as a soft halo instead of a blurred rectangle clipped by overflow-hidden. */}
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 h-[90px] w-[90px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-40 blur-2xl"
        style={{ background: `radial-gradient(closest-side, ${GREEN}33, transparent)` }}
      />

      {/* Isometric scanner pad */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <div
          className="relative h-[130px] w-[130px]"
          style={{
            transformStyle: "preserve-3d",
            transform: `rotateX(55deg) rotateZ(${mirrored ? 35 : -35}deg)`,
          }}
        >
          {/* Ghost slabs — dashed wireframe layers below the pad. Slightly larger than the
           * pad so the dashed frame peeks out on every side: at inset-0 the pad occludes the
           * back edge in projection and the outline reads as broken. */}
          <div
            className="absolute -inset-2 rounded-[20px] border border-dashed border-white/20"
            style={{ transform: "translateZ(-20px)" }}
          />
          <div
            className="absolute -inset-3 rounded-[22px] border border-dashed border-white/10"
            style={{ transform: "translateZ(-34px)" }}
          />

          {/* Drop shadow beneath the slab — below both ghost slabs in depth so the blurred
           * black doesn't paint over their dashed edges. */}
          <div
            className="absolute -inset-2 rounded-[24px] bg-black/50 blur-lg"
            style={{ transform: "translateZ(-40px)" }}
          />
          {/* Slab underside */}
          <div
            className="absolute inset-0 rounded-[16px] bg-[#1a1d21]"
            style={{ transform: "translateZ(-10px)" }}
          />
          {/* Side walls — extruded thickness along the front edges */}
          <div
            className="absolute right-[16px] bottom-0 left-[16px] h-[10px]"
            style={{
              transformOrigin: "bottom",
              transform: "rotateX(90deg)",
              background: "linear-gradient(180deg, #3a3e44 0%, #1e2025 100%)",
            }}
          />
          {/* The mirrored tilt exposes the slab's left edge instead of its right. */}
          {mirrored ? (
            <div
              className="absolute top-[16px] bottom-[16px] left-0 w-[10px]"
              style={{
                transformOrigin: "left",
                transform: "rotateY(90deg)",
                background: "linear-gradient(270deg, #2a2e33 0%, #16181c 100%)",
              }}
            />
          ) : (
            <div
              className="absolute top-[16px] right-0 bottom-[16px] w-[10px]"
              style={{
                transformOrigin: "right",
                transform: "rotateY(-90deg)",
                background: "linear-gradient(90deg, #2a2e33 0%, #16181c 100%)",
              }}
            />
          )}

          {/* Top face — the scanner surface */}
          <div className="absolute inset-0 overflow-hidden rounded-[16px] border border-white/20 bg-[#0A0B0D]">
            <div className="absolute inset-0" style={{ backgroundColor: `${GREEN}0D` }} />
            {/* Directional light across the face */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: "linear-gradient(135deg, rgba(247,247,248,0.06), transparent 45%)",
              }}
            />

            {/* Scanner frame — corner brackets */}
            {(
              [
                "top-2 left-2 border-t border-l",
                "top-2 right-2 border-t border-r",
                "bottom-2 left-2 border-b border-l",
                "bottom-2 right-2 border-b border-r",
              ] as const
            ).map((corner) => (
              <span
                key={corner}
                className={cn("absolute h-3 w-3 rounded-[1px] border-white/30", corner)}
              />
            ))}

            {/* The subject being scanned */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon
                className="size-14"
                style={{ color: GREEN, filter: `drop-shadow(0 0 10px ${GREEN}40)` }}
                aria-hidden
              />
            </div>

            {/* Sweeping scan line */}
            <motion.div
              className="absolute inset-x-3 h-[2px] rounded-full"
              style={{
                background: `linear-gradient(90deg, transparent, ${GREEN}, transparent)`,
                boxShadow: `0 0 12px ${GREEN}66`,
                top: "18%",
              }}
              animate={active ? { top: ["18%", "78%", "18%"] } : undefined}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Readout */}
            <div className="absolute inset-x-2 bottom-[14px] flex items-center justify-center gap-1.5">
              <span className="block font-mono text-[7.5px] tracking-[0.14em] text-white">
                {readoutLabel}
              </span>
              <span
                className="block font-mono text-[7.5px] font-semibold tracking-[0.14em]"
                style={{ color: GREEN }}
              >
                {readoutValue}
              </span>
            </div>

            {/* Soft breathing glow */}
            {!reduced && (
              <motion.div
                className="pointer-events-none absolute inset-0 rounded-[16px] border"
                style={{
                  borderColor: `${GREEN}59`,
                  boxShadow: `0 0 18px ${GREEN}2E, inset 0 0 14px ${GREEN}17`,
                }}
                initial={{ opacity: 0 }}
                animate={active ? { opacity: [0, 0.6, 0] } : undefined}
                transition={{ duration: 4.8, delay: 0.9, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  variant: "fingerprint";
  layout?: "rail" | "horizontal";
}

export default function ToolCta({ variant, layout = "rail" }: Props) {
  const { icon, readoutLabel, readoutValue, heading, body } = FINGERPRINT_CTA;
  const posthog = usePostHog();
  const reduced = useReducedMotion() ?? false;
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { margin: "100px 0px" });
  const active = !reduced && inView;

  const productLinks = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <Link
        href={ROUTES.productMobileProxies}
        onClick={() =>
          posthog.capture("tool_cta_clicked", { tool: variant, target: "mobile_proxies" })
        }
        className="text-brand-green inline-flex items-center gap-1 text-sm hover:underline"
      >
        Mobile proxies
        <ArrowRight className="size-3.5" aria-hidden />
      </Link>
      <Link
        href={ROUTES.productResidentialProxies}
        onClick={() =>
          posthog.capture("tool_cta_clicked", { tool: variant, target: "residential_proxies" })
        }
        className="text-brand-green inline-flex items-center gap-1 text-sm hover:underline"
      >
        Residential proxies
        <ArrowRight className="size-3.5" aria-hidden />
      </Link>
    </div>
  );

  if (layout === "horizontal") {
    // Wide variant: text-only pitch for hero asides — no border or fill (boxing read like a
    // banner ad), and no scanner pad: beside the hero copy it crowded the text at laptop
    // widths and read as clutter, so the divider and copy carry the panel on their own.
    return (
      <aside ref={rootRef} className="relative w-full" aria-label="Proxidize proxies">
        <div className="flex min-w-0 flex-col justify-center gap-2">
          <span className="font-mono text-[9px] font-semibold tracking-widest text-white/60 uppercase">
            Why Proxidize
          </span>
          <h2 className="text-foreground text-base leading-snug font-semibold">{heading}</h2>
          <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">{body}</p>
          {productLinks}
        </div>
      </aside>
    );
  }

  return (
    <aside
      ref={rootRef}
      className="border-border bg-card relative w-full shrink-0 self-start overflow-hidden rounded-xl border lg:sticky lg:top-[8.5rem] lg:w-96"
      aria-label="Proxidize proxies"
    >
      <ScannerStage
        icon={icon}
        readoutLabel={readoutLabel}
        readoutValue={readoutValue}
        active={active}
        reduced={reduced}
        className="h-[210px]"
      />
      <div className="relative flex flex-col gap-3 px-5 pt-1 pb-5">
        <span className="text-muted-foreground font-mono text-[9px] font-semibold tracking-widest uppercase">
          Why Proxidize
        </span>
        <h2 className="text-foreground text-lg leading-snug font-semibold">{heading}</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
        <a
          href={FREE_TRIAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            posthog.capture("tool_cta_clicked", { tool: variant, target: "free_trial" })
          }
          className="bg-brand-green text-background mt-1 inline-flex w-fit items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
        >
          {FREE_TRIAL_LABEL}
          <ArrowRight className="size-4" aria-hidden />
        </a>
        {productLinks}
      </div>
    </aside>
  );
}
